import React, { useEffect, useState } from "react";

import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import TiptapEditor from "./TiptapEditor";
import QuestionPaperPreview from "./QuestionPaperPreview";
import QuestionPaperPrint from "./QuestionPaperPrint";
import AuthService from "../../Auth/AuthService";

/* =========================================================
   Create Empty Question
========================================================= */

const createQuestion = () => ({
    id: `question-${Date.now()}-${Math.random()}`,
    content: {
        type: "doc",
        content: [
            {
                type: "paragraph",
            },
        ],
    },
    marks: 1,
});

/* =========================================================
   Create Empty Section
========================================================= */

const createSection = (sectionNumber = 1) => ({
    id: `section-${Date.now()}-${Math.random()}`,
    name: `Section ${String.fromCharCode(64 + sectionNumber)}`,
    instructions: "",
    questions: [createQuestion()],
});

/* =========================================================
   Empty Paper
========================================================= */

const createEmptyPaper = () => ({
    id: null,

    title: "",

    examName: "",

    className: "",

    subject: "",

    academicYear: "",

    duration: "",

    totalMarks: "",

    status: "DRAFT",

    sections: [createSection(1)],
});

/* =========================================================
   Component
========================================================= */

const QuestionPaperBuilder = ({
    paperId = null,
    onBack = null,
}) => {
    const [paper, setPaper] = useState(createEmptyPaper);

    const [loading, setLoading] = useState(false);

    const [saving, setSaving] = useState(false);

    const [showPreview, setShowPreview] = useState(false);

    const [showPrint, setShowPrint] = useState(false);

    /* =======================================================
       Load Existing Paper
    ======================================================= */

    useEffect(() => {
        if (paperId) {
            loadQuestionPaper(paperId);
        }
    }, [paperId]);

    const loadQuestionPaper = async (id) => {
        try {
            setLoading(true);

            const response = await AuthService.getAuth(
                AuthService.pathUrl("GET", `/question-papers/${id}`));

            const data = response;

            /*
             * Make sure sections/questions always exist.
             */
            const normalizedPaper = {
                ...data,

                sections:
                    data.sections?.map((section, sectionIndex) => ({
                        ...section,

                        id:
                            section.id ||
                            `section-${Date.now()}-${sectionIndex}`,

                        name:
                            section.name ||
                            `Section ${String.fromCharCode(
                                65 + sectionIndex
                            )}`,

                        instructions:
                            section.instructions || "",

                        questions:
                            section.questions?.map(
                                (question, questionIndex) => ({
                                    ...question,

                                    id:
                                        question.id ||
                                        `question-${Date.now()}-${questionIndex}`,

                                    marks:
                                        question.marks ?? 1,

                                    content:
                                        question.content || {
                                            type: "doc",
                                            content: [
                                                {
                                                    type: "paragraph",
                                                },
                                            ],
                                        },
                                })
                            ) || [],
                    })) || [createSection(1)],
            };

            setPaper(normalizedPaper);
        } catch (error) {
            console.error(
                "Error loading question paper:",
                error
            );

            alert(
                error.message ||
                "Unable to load question paper."
            );
        } finally {
            setLoading(false);
        }
    };

    /* =======================================================
       Handle Basic Paper Fields
    ======================================================= */

    const handlePaperChange = (field, value) => {
        setPaper((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    /* =======================================================
       Add Section
    ======================================================= */

    const addSection = () => {
        setPaper((previous) => {
            const nextSectionNumber =
                previous.sections.length + 1;

            return {
                ...previous,

                sections: [
                    ...previous.sections,

                    createSection(nextSectionNumber),
                ],
            };
        });
    };

    /* =======================================================
       Delete Section
    ======================================================= */

    const deleteSection = (sectionIndex) => {
        if (paper.sections.length === 1) {
            alert(
                "At least one section is required."
            );

            return;
        }

        const confirmed = window.confirm(
            "Are you sure you want to delete this section and all its questions?"
        );

        if (!confirmed) {
            return;
        }

        setPaper((previous) => ({
            ...previous,

            sections: previous.sections.filter(
                (_, index) => index !== sectionIndex
            ),
        }));
    };

    /* =======================================================
       Update Section
    ======================================================= */

    const updateSection = (
        sectionIndex,
        field,
        value
    ) => {
        setPaper((previous) => ({
            ...previous,

            sections: previous.sections.map(
                (section, index) =>
                    index === sectionIndex
                        ? {
                            ...section,
                            [field]: value,
                        }
                        : section
            ),
        }));
    };

    /* =======================================================
       Add Question
    ======================================================= */

    const addQuestion = (sectionIndex) => {
        setPaper((previous) => ({
            ...previous,

            sections: previous.sections.map(
                (section, index) =>
                    index === sectionIndex
                        ? {
                            ...section,

                            questions: [
                                ...(section.questions || []),
                                createQuestion(),
                            ],
                        }
                        : section
            ),
        }));
    };

    /* =======================================================
       Delete Question
    ======================================================= */

    const deleteQuestion = (
        sectionIndex,
        questionIndex
    ) => {
        const section =
            paper.sections[sectionIndex];

        if (
            !section ||
            section.questions.length === 1
        ) {
            alert(
                "At least one question is required in a section."
            );

            return;
        }

        setPaper((previous) => ({
            ...previous,

            sections: previous.sections.map(
                (currentSection, sIndex) =>
                    sIndex === sectionIndex
                        ? {
                            ...currentSection,

                            questions:
                                currentSection.questions.filter(
                                    (_, qIndex) =>
                                        qIndex !== questionIndex
                                ),
                        }
                        : currentSection
            ),
        }));
    };

    /* =======================================================
       Update Question Content
    ======================================================= */

    const updateQuestionContent = (
        sectionIndex,
        questionIndex,
        content
    ) => {
        setPaper((previous) => ({
            ...previous,

            sections: previous.sections.map(
                (section, sIndex) =>
                    sIndex === sectionIndex
                        ? {
                            ...section,

                            questions:
                                section.questions.map(
                                    (question, qIndex) =>
                                        qIndex === questionIndex
                                            ? {
                                                ...question,
                                                content,
                                            }
                                            : question
                                ),
                        }
                        : section
            ),
        }));
    };

    /* =======================================================
       Update Question Marks
    ======================================================= */

    const updateQuestionMarks = (
        sectionIndex,
        questionIndex,
        marks
    ) => {
        setPaper((previous) => ({
            ...previous,

            sections: previous.sections.map(
                (section, sIndex) =>
                    sIndex === sectionIndex
                        ? {
                            ...section,

                            questions:
                                section.questions.map(
                                    (question, qIndex) =>
                                        qIndex === questionIndex
                                            ? {
                                                ...question,

                                                marks:
                                                    marks === ""
                                                        ? ""
                                                        : Number(marks),
                                            }
                                            : question
                                ),
                        }
                        : section
            ),
        }));
    };

    /* =======================================================
       Calculate Total Marks
    ======================================================= */

    const calculateTotalMarks = () => {
        return paper.sections.reduce(
            (sectionTotal, section) => {
                const questionTotal =
                    section.questions.reduce(
                        (total, question) =>
                            total +
                            Number(question.marks || 0),
                        0
                    );

                return sectionTotal + questionTotal;
            },
            0
        );
    };

    /* =======================================================
       Calculate Total Questions
    ======================================================= */

    const calculateTotalQuestions = () => {
        return paper.sections.reduce(
            (total, section) =>
                total +
                (section.questions?.length || 0),
            0
        );
    };

    /* =======================================================
       Prepare Payload
    ======================================================= */

    const preparePayload = () => {
        return {
            title: paper.title,

            examName: paper.examName,

            className: paper.className,

            subject: paper.subject,

            academicYear: paper.academicYear,

            duration: paper.duration,

            totalMarks:
                paper.totalMarks === "" ||
                    paper.totalMarks === null
                    ? calculateTotalMarks()
                    : Number(paper.totalMarks),

            sections: paper.sections.map(
                (section) => ({
                    name: section.name,

                    instructions:
                        section.instructions || "",

                    questions:
                        section.questions.map(
                            (question) => ({
                                content:
                                    question.content,

                                marks:
                                    Number(question.marks || 0),
                            })
                        ),
                })
            ),
        };
    };

    /* =======================================================
       Save / Create Paper
    ======================================================= */

    const savePaper = async () => {
        try {
            if (!paper.title?.trim()) {
                alert(
                    "Please enter Question Paper Title."
                );

                return;
            }

            if (!paper.examName?.trim()) {
                alert(
                    "Please enter Exam Name."
                );

                return;
            }

            if (!paper.subject?.trim()) {
                alert(
                    "Please enter Subject."
                );

                return;
            }

            setSaving(true);

            const payload = preparePayload();

            const response = await AuthService.postAuth(
                AuthService.pathUrl("POST", "/question-papers"),
                payload
            );

            if (!response.ok) {
                const errorText =
                    await response.text();

                throw new Error(
                    errorText ||
                    `Save failed. Status: ${response.status}`
                );
            }

            const savedPaper =
                await response.json();

            setPaper(savedPaper);

            alert(
                "Question paper saved successfully."
            );
        } catch (error) {
            console.error(
                "Save question paper error:",
                error
            );

            alert(
                error.message ||
                "Unable to save question paper."
            );
        } finally {
            setSaving(false);
        }
    };

    /* =======================================================
       Update Existing Paper
    ======================================================= */

    const updateQuestionPaper = async () => {
        try {
            if (!paper.id) {
                await savePaper();
                return;
            }

            if (!paper.title?.trim()) {
                alert(
                    "Please enter Question Paper Title."
                );

                return;
            }

            setSaving(true);

            const payload = preparePayload();

            const response = await AuthService.putAuth(
                AuthService.pathUrl("PUT",`/question-papers/${paper.id}`),
                payload,
            );

            if (!response.ok) {
                const errorText =
                    await response.text();

                throw new Error(
                    errorText ||
                    `Update failed. Status: ${response.status}`
                );
            }

            const updatedPaper =
                await response.json();

            setPaper(updatedPaper);

            alert(
                "Question paper updated successfully."
            );
        } catch (error) {
            console.error(
                "Update question paper error:",
                error
            );

            alert(
                error.message ||
                "Unable to update question paper."
            );
        } finally {
            setSaving(false);
        }
    };

    /* =======================================================
       Save Button Handler
    ======================================================= */

    const handleSave = async () => {
        if (paper.id) {
            await updateQuestionPaper();
        } else {
            await savePaper();
        }
    };

    /* =======================================================
       Loading
    ======================================================= */

    if (loading) {
        return (
            <Box
                sx={{
                    p: 4,
                    textAlign: "center",
                }}
            >
                <Typography>
                    Loading question paper...
                </Typography>
            </Box>
        );
    }

    /* =======================================================
       PRINT SCREEN
    ======================================================= */

    if (showPrint) {
        return (
            <QuestionPaperPrint
                paper={paper}
                schoolName="JMK School"
                schoolLogo="/logo.png"
                onBack={() =>
                    setShowPrint(false)
                }
            />
        );
    }

    /* =======================================================
       PREVIEW SCREEN
    ======================================================= */

    if (showPreview) {
        return (
            <Box sx={{ width: "100%" }}>

                <Box
                    sx={{
                        position: "fixed",
                        top: 16,
                        right: 16,
                        zIndex: 9999,
                    }}
                >
                    <Button
                        variant="contained"
                        startIcon={<ArrowBackIcon />}
                        onClick={() =>
                            setShowPreview(false)
                        }
                    >
                        Back to Editor
                    </Button>
                </Box>

                <QuestionPaperPreview
                    paper={paper}
                    onClose={() =>
                        setShowPreview(false)
                    }
                />

            </Box>
        );
    }

    /* =======================================================
       MAIN BUILDER
    ======================================================= */

    return (
        <Box
            sx={{
                width: "100%",
                p: {
                    xs: 1,
                    sm: 2,
                    md: 3,
                },
                backgroundColor: "#f5f6f8",
                minHeight: "100vh",
            }}
        >

            {/* =====================================================
          HEADER
      ===================================================== */}

            <Card
                sx={{
                    mb: 3,
                    borderRadius: 2,
                }}
            >
                <CardContent>

                    <Stack
                        direction={{
                            xs: "column",
                            sm: "row",
                        }}
                        justifyContent="space-between"
                        alignItems={{
                            xs: "flex-start",
                            sm: "center",
                        }}
                        spacing={2}
                    >

                        <Box>
                            <Typography
                                variant="h5"
                                fontWeight={700}
                            >
                                Question Paper Builder
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Create and manage
                                question papers
                            </Typography>
                        </Box>

                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={1}
                            width={{
                                xs: "100%",
                                sm: "auto",
                            }}
                        >

                            {onBack && (
                                <Button
                                    variant="outlined"
                                    startIcon={
                                        <ArrowBackIcon />
                                    }
                                    onClick={onBack}
                                >
                                    Back
                                </Button>
                            )}

                            <Button
                                variant="outlined"
                                startIcon={
                                    <VisibilityIcon />
                                }
                                onClick={() =>
                                    setShowPreview(true)
                                }
                            >
                                Preview
                            </Button>

                            <Button
                                variant="contained"
                                startIcon={
                                    <PrintIcon />
                                }
                                onClick={() =>
                                    setShowPrint(true)
                                }
                            >
                                Print
                            </Button>

                            <Button
                                variant="contained"
                                color="success"
                                startIcon={
                                    <SaveIcon />
                                }
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving
                                    ? "Saving..."
                                    : paper.id
                                        ? "Update Paper"
                                        : "Save Paper"}
                            </Button>

                        </Stack>

                    </Stack>

                </CardContent>
            </Card>

            {/* =====================================================
          PAPER DETAILS
      ===================================================== */}

            <Card
                sx={{
                    mb: 3,
                    borderRadius: 2,
                }}
            >

                <CardContent>

                    <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ mb: 2 }}
                    >
                        Paper Details
                    </Typography>

                    <Grid
                        container
                        spacing={2}
                    >

                        {/* Title */}

                        <Grid
                            item
                            xs={12}
                            md={6}
                        >
                            <TextField
                                fullWidth
                                label="Question Paper Title"
                                value={paper.title || ""}
                                onChange={(e) =>
                                    handlePaperChange(
                                        "title",
                                        e.target.value
                                    )
                                }
                                placeholder="Mathematics Question Paper"
                            />
                        </Grid>

                        {/* Exam */}

                        <Grid
                            item
                            xs={12}
                            md={6}
                        >
                            <TextField
                                fullWidth
                                label="Exam Name"
                                value={
                                    paper.examName || ""
                                }
                                onChange={(e) =>
                                    handlePaperChange(
                                        "examName",
                                        e.target.value
                                    )
                                }
                                placeholder="Half Yearly Examination"
                            />
                        </Grid>

                        {/* Class */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                        >
                            <TextField
                                fullWidth
                                label="Class"
                                value={
                                    paper.className || ""
                                }
                                onChange={(e) =>
                                    handlePaperChange(
                                        "className",
                                        e.target.value
                                    )
                                }
                                placeholder="Class 9"
                            />
                        </Grid>

                        {/* Subject */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                        >
                            <TextField
                                fullWidth
                                label="Subject"
                                value={
                                    paper.subject || ""
                                }
                                onChange={(e) =>
                                    handlePaperChange(
                                        "subject",
                                        e.target.value
                                    )
                                }
                                placeholder="Mathematics"
                            />
                        </Grid>

                        {/* Academic Year */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                        >
                            <TextField
                                fullWidth
                                label="Academic Year"
                                value={
                                    paper.academicYear || ""
                                }
                                onChange={(e) =>
                                    handlePaperChange(
                                        "academicYear",
                                        e.target.value
                                    )
                                }
                                placeholder="2026-2027"
                            />
                        </Grid>

                        {/* Duration */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                        >
                            <TextField
                                fullWidth
                                label="Duration"
                                value={
                                    paper.duration || ""
                                }
                                onChange={(e) =>
                                    handlePaperChange(
                                        "duration",
                                        e.target.value
                                    )
                                }
                                placeholder="3 Hours"
                            />
                        </Grid>

                        {/* Total Marks */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                        >
                            <TextField
                                fullWidth
                                type="number"
                                label="Maximum Marks"
                                value={
                                    paper.totalMarks ?? ""
                                }
                                onChange={(e) =>
                                    handlePaperChange(
                                        "totalMarks",
                                        e.target.value
                                    )
                                }
                                placeholder={String(
                                    calculateTotalMarks()
                                )}
                            />
                        </Grid>

                    </Grid>

                </CardContent>

            </Card>

            {/* =====================================================
          SUMMARY
      ===================================================== */}

            <Card
                sx={{
                    mb: 3,
                    borderRadius: 2,
                }}
            >

                <CardContent>

                    <Stack
                        direction={{
                            xs: "column",
                            sm: "row",
                        }}
                        spacing={3}
                    >

                        <Typography>
                            <strong>
                                Sections:
                            </strong>{" "}
                            {paper.sections.length}
                        </Typography>

                        <Typography>
                            <strong>
                                Questions:
                            </strong>{" "}
                            {calculateTotalQuestions()}
                        </Typography>

                        <Typography>
                            <strong>
                                Calculated Marks:
                            </strong>{" "}
                            {calculateTotalMarks()}
                        </Typography>

                    </Stack>

                </CardContent>

            </Card>

            {/* =====================================================
          SECTIONS
      ===================================================== */}

            {paper.sections.map(
                (section, sectionIndex) => (

                    <Card
                        key={
                            section.id ||
                            sectionIndex
                        }
                        sx={{
                            mb: 3,
                            borderRadius: 2,
                        }}
                    >

                        {/* =================================================
                SECTION HEADER
            ================================================= */}

                        <CardContent>

                            <Stack
                                direction={{
                                    xs: "column",
                                    sm: "row",
                                }}
                                justifyContent="space-between"
                                alignItems={{
                                    xs: "stretch",
                                    sm: "center",
                                }}
                                spacing={2}
                            >

                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                >
                                    Section{" "}
                                    {String.fromCharCode(
                                        65 + sectionIndex
                                    )}
                                </Typography>

                                <IconButton
                                    color="error"
                                    onClick={() =>
                                        deleteSection(
                                            sectionIndex
                                        )
                                    }
                                    title="Delete Section"
                                >
                                    <DeleteIcon />
                                </IconButton>

                            </Stack>

                            <Divider sx={{ my: 2 }} />

                            {/* Section Details */}

                            <Grid
                                container
                                spacing={2}
                            >

                                <Grid
                                    item
                                    xs={12}
                                    md={5}
                                >
                                    <TextField
                                        fullWidth
                                        label="Section Name"
                                        value={
                                            section.name || ""
                                        }
                                        onChange={(e) =>
                                            updateSection(
                                                sectionIndex,
                                                "name",
                                                e.target.value
                                            )
                                        }
                                        placeholder="Section A"
                                    />
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    md={7}
                                >
                                    <TextField
                                        fullWidth
                                        label="Instructions"
                                        value={
                                            section.instructions ||
                                            ""
                                        }
                                        onChange={(e) =>
                                            updateSection(
                                                sectionIndex,
                                                "instructions",
                                                e.target.value
                                            )
                                        }
                                        placeholder="Answer all questions."
                                    />
                                </Grid>

                            </Grid>

                        </CardContent>

                        {/* =================================================
                QUESTIONS
            ================================================= */}

                        <Box
                            sx={{
                                px: {
                                    xs: 2,
                                    md: 3,
                                },
                                pb: 3,
                            }}
                        >

                            {section.questions.map(
                                (
                                    question,
                                    questionIndex
                                ) => (

                                    <Card
                                        key={
                                            question.id ||
                                            questionIndex
                                        }
                                        variant="outlined"
                                        sx={{
                                            mb: 2,
                                            borderRadius: 2,
                                        }}
                                    >

                                        <CardContent>

                                            {/* Question Header */}

                                            <Stack
                                                direction={{
                                                    xs: "column",
                                                    sm: "row",
                                                }}
                                                justifyContent="space-between"
                                                alignItems={{
                                                    xs: "stretch",
                                                    sm: "center",
                                                }}
                                                spacing={1}
                                                sx={{
                                                    mb: 2,
                                                }}
                                            >

                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight={700}
                                                >
                                                    Question{" "}
                                                    {questionIndex + 1}
                                                </Typography>

                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                >

                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        label="Marks"
                                                        value={
                                                            question.marks ??
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            updateQuestionMarks(
                                                                sectionIndex,
                                                                questionIndex,
                                                                e.target.value
                                                            )
                                                        }
                                                        sx={{
                                                            width: 110,
                                                        }}
                                                        inputProps={{
                                                            min: 0,
                                                            step: 0.5,
                                                        }}
                                                    />

                                                    <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                            deleteQuestion(
                                                                sectionIndex,
                                                                questionIndex
                                                            )
                                                        }
                                                        title="Delete Question"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>

                                                </Stack>

                                            </Stack>

                                            {/* Tiptap Editor */}

                                            <TiptapEditor
                                                content={
                                                    question.content
                                                }
                                                onChange={(content) =>
                                                    updateQuestionContent(
                                                        sectionIndex,
                                                        questionIndex,
                                                        content
                                                    )
                                                }
                                            />

                                        </CardContent>

                                    </Card>

                                )
                            )}

                            {/* Add Question */}

                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                    addQuestion(
                                        sectionIndex
                                    )
                                }
                            >
                                Add Question
                            </Button>

                        </Box>

                    </Card>

                )
            )}

            {/* =====================================================
          ADD SECTION
      ===================================================== */}

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 5,
                }}
            >

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addSection}
                    size="large"
                >
                    Add Section
                </Button>

            </Box>

        </Box>
    );
};

export default QuestionPaperBuilder;